<docs>
The form-field label row: label text, required asterisk and an optional info tooltip. A building
block used by ZoneTextInput / ZoneTextArea / ZoneCheckbox / ZoneSelect, which render a ZoneLabel for
you: prefer those, and use ZoneLabel directly only when composing a custom field, or inside another
field's `#label` slot when you need extra markup beside the label. No v-model, no events.

## Notes

- The info-icon tooltip renders through ZoneTooltip (floating/teleported), so its content never
  appears in `wrapper.html()`: assert on the trigger, the ZoneIcon inside `.v-popper`
  (`.v-popper [data-testid="icon"]`).
- Figma "Label wrapper", identical across every Zone field blueprint: the row is
  `flex items-center gap-1`; the label is uppercase, 12px, 16px line-height, font-medium at gray-400
  (#4B5563), red-600 (#E02424) when invalid; the info glyph is `info` at 18px in the same colour.
- The required `*` lives inside the `<label>` with `aria-hidden` and carries no colour of its own, so
  it inherits the label's: grey-400 normally, red-600 when `invalid`, never a red mark beside a grey
  label. `ml-0.5` is the whole gap (0.2rem in this theme's scale, a space's width at 12px).
  ZoneCheckbox's inline label and ZoneSelect's own label row repeat this markup verbatim (all three
  render a `[data-testid="required-asterisk"]`): change all three together.
- Every typographic property is declared on the `<label>` itself, never inherited from the row, and
  has to stay that way. Two global bare-element rules load on every app page and beat anything
  merely inherited: `bs5/bridge.scss` (`label { margin-bottom: 5px; font-weight: 700 }`) and
  `custom_bootstrap/_forms` (`label { font-weight: 500 }`), so `m-0` and `font-medium` on the
  `<label>` are load-bearing. This file once put the colour on the row while the `<label>` carried
  the legacy `.control-label` class, whose own `color: #6b7280` and `font-size: 11px` won: every
  Zone field label rendered at gray-500/11px and `invalid` never reddened the text. Do not
  reintroduce `.control-label`, and do not move colour, size, weight or margin back onto the row.
- That bug was invisible in both test environments: jsdom loads no CSS, and /zone/vue-docs does not
  load application.css. Verify label changes on a real app page.
- The row keeps `mb-1`: ZoneTextArea, ZoneToggle and ZonePhoneField have no column gap of their own
  and rely on it for label-to-field spacing. Moving every field to Figma's `gap-1` field root is a
  separate change.
</docs>

<template>
  <div class="flex items-center gap-1">
    <label
      :for="htmlFor"
      :class="labelClasses"
    >
      <slot>{{ label }}</slot>
      <span
        v-if="required"
        class="ml-0.5"
        aria-hidden="true"
        data-testid="required-asterisk"
      >*</span>
    </label>
    <ZoneIcon
      v-if="icon"
      :icon="iconName"
      :tooltip="tooltip"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import ZoneIcon, { type IconName } from './Icon.vue';

defineOptions({ name: 'ZoneLabel' });

const props = defineProps({
  /** The label text; the default slot overrides it. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Renders a `*` after the label, inside the `<label>` and in its own colour (it follows the text
   * to red-600 when `invalid`), `aria-hidden`.
   */
  required: {
    type: Boolean,
    default: false,
  },
  /**
   * Hover tooltip on the info icon. Pass `icon` too, or there is nothing to hover: the fields do
   * `:icon="Boolean(tooltip)"`.
   */
  tooltip: {
    type: String,
    default: null,
  },
  /** Error state: label text and info icon turn red-600. */
  invalid: {
    type: Boolean,
    default: false,
  },
  /**
   * Render a ZoneIcon after the text, inside the `<label>`, so it takes the label's colour (grey,
   * or red when `invalid`). Off by default; the fields turn it on when they have a tooltip.
   */
  icon: {
    type: Boolean,
    default: false,
  },
  /** The ZoneIcon rendered by `icon`. */
  iconName: {
    type: String as PropType<IconName>,
    default: 'info',
  },
  /**
   * Sets `<label for>` so clicking the label focuses the field: pass the field's id. Omit for no
   * `for` attribute.
   */
  htmlFor: {
    type: String,
    default: null,
  },
});

defineSlots<{
  /** The label content; overrides `label`. */
  default?(): any;
}>();

const labelClasses = computed<string>(() => {
  const color = props.invalid ? 'text-red-600' : 'text-gray-400';

  return `m-0 text-xs font-medium uppercase leading-4 ${color}`;
});
</script>
